import FetchWrapper from '../Common/Wrappers/FetchWrapper';
import { Service } from '../Common/service.enum';
import Logging from '../../Logging/Logging';
import {
  SellerCollection,
  Seller,
  ProductCollection,
  Product,
  ExemptionTypeRequest,
  ExemptionTypeCollection,
  JurisdictionCollection,
  JurisdictionRequest,
} from '../../..';
import { filtersToStandardQueryParams, sortModelToStandardQueryParam } from '../Common/filterSorting';

export default class ECWizardRepo {
  private fetch: FetchWrapper;

  constructor(fetch: FetchWrapper) {
    this.fetch = fetch;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSellersV41(
    startRow: number,
    endRow: number,
    filterModel: string[] = null,
    sortModel: string[] = null,
    customFilter: string = null,
  ): Promise<SellerCollection> {
    const apiServiceEndpoint = 'portal/v4.1/sellers';
    const filterQueryParams = customFilter || filtersToStandardQueryParams(filterModel);
    const sortQueryParam = sortModelToStandardQueryParam(sortModel);
    const numberRows = endRow > startRow ? endRow - startRow : 0;
    const pagingQueryParam = `&$top=${numberRows}&$skip=${startRow}&$count=true`;

    let queryParams = '';
    if (filterQueryParams && sortQueryParam) {
      queryParams = `?${filterQueryParams}&${sortQueryParam}`;
    } else if (filterQueryParams) {
      queryParams = `?${filterQueryParams}`;
    } else if (sortQueryParam) {
      queryParams = `?${sortQueryParam}`;
    }

    // Add the Paging Parameters
    if (pagingQueryParam) {
      queryParams += queryParams === '' ? `?${pagingQueryParam}` : pagingQueryParam;
    }

    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<SellerCollection>(Service.ECM, url);
  }

  getSellerV41(sellerUuid: string): Promise<Seller> {
    const apiServiceEndpoint = `portal/v4.1/sellers/${sellerUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<Seller>(Service.ECM, url);
  }

  addSellerV41(seller: Seller): Promise<Headers> {
    const apiServiceEndpoint = 'portal/v4.1/sellers';

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('POST', url, seller);
    // Return the HTTP Headers instead of the Body.  The newly inserted Seller Uuid will be in the 'Location' Header
    return this.fetch.postToServiceReturnHeaders<void>(Service.ECM, url, seller);
  }

  editSellerV41(seller: Seller): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/sellers/${seller.uuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('PUT', url, seller);
    return this.fetch.putToService<void>(Service.ECM, url, seller);
  }

  deleteSellerV41(sellerUuid: string): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/sellers/${sellerUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('DELETE', url);
    return this.fetch.delFromService<void>(Service.ECM, url);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getProductsV41(
    startRow: number,
    endRow: number,
    filterModel: string[] = null,
    sortModel: string[] = null,
    customFilter: string = null,
  ): Promise<ProductCollection> {
    const apiServiceEndpoint = 'portal/v4.1/products';
    const filterQueryParams = customFilter || filtersToStandardQueryParams(filterModel);
    const sortQueryParam = sortModelToStandardQueryParam(sortModel);
    const numberRows = endRow > startRow ? endRow - startRow : 0;
    const pagingQueryParam = `&$top=${numberRows}&$skip=${startRow}&$count=true`;

    let queryParams = '';
    if (filterQueryParams && sortQueryParam) {
      queryParams = `?${filterQueryParams}&${sortQueryParam}`;
    } else if (filterQueryParams) {
      queryParams = `?${filterQueryParams}`;
    } else if (sortQueryParam) {
      queryParams = `?${sortQueryParam}`;
    }

    // Add the Paging Parameters
    if (pagingQueryParam) {
      queryParams += queryParams === '' ? `?${pagingQueryParam}` : pagingQueryParam;
    }

    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<ProductCollection>(Service.ECM, url);
  }

  getProductV41(productUuid: string): Promise<Product> {
    const apiServiceEndpoint = `portal/v4.1/products/${productUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<Product>(Service.ECM, url);
  }

  addProductV41(product: Product): Promise<Headers> {
    const apiServiceEndpoint = 'portal/v4.1/products';

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('POST', url, product);
    // Return the HTTP Headers instead of the Body.  The newly inserted Product Uuid will be in the 'Location' Header
    return this.fetch.postToServiceReturnHeaders<void>(Service.ECM, url, product);
  }

  editProductV41(product: Product): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/products/${product.uuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('PUT', url, product);
    return this.fetch.putToService<void>(Service.ECM, url, product);
  }

  deleteProductV41(productUuid: string): Promise<void> {
    const apiServiceEndpoint = `portal/v4.1/products/${productUuid}`;

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('DELETE', url);
    return this.fetch.delFromService<void>(Service.ECM, url);
  }

  getExemptionTypesV41(): Promise<ExemptionTypeCollection> {
    const apiServiceEndpoint = 'portal/v4.1/settings/exemption-types';

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<ExemptionTypeCollection>(Service.ECM, url);
  }

  setExemptionTypesV41(exemptionTypeIds: number[]): Promise<void> {
    const apiServiceEndpoint = 'portal/v4.1/settings/exemption-types';

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;
    const exemptionTypesRequest: ExemptionTypeRequest = {
      exemptionTypeIds,
    };

    Logging.Info('PUT', url, exemptionTypesRequest);
    return this.fetch.putToService<void>(Service.ECM, url, exemptionTypesRequest);
  }

  getJurisdictionsV41(startRow: number, endRow: number): Promise<JurisdictionCollection> {
    const apiServiceEndpoint = 'portal/v4.1/settings/jurisdictions';
    const numberRows = endRow > startRow ? endRow - startRow : 0;
    const queryParams = '$filter=isAssignedForms eq \'true\'';
    const pagingQueryParam = `&$top=${numberRows}&$skip=${startRow}&$count=true`;

    const url = `${apiServiceEndpoint}?${queryParams}${pagingQueryParam}`;

    Logging.Info('GET', url);
    return this.fetch.getFromService<JurisdictionCollection>(Service.ECM, url);
  }

  setJurisdictionsV41(jurisdictionIds: number[]): Promise<void> {
    const apiServiceEndpoint = 'portal/v4.1/settings/jurisdictions';

    const queryParams = '';
    const url = `${apiServiceEndpoint}${queryParams}`;

    const jurisdictionsRequest: JurisdictionRequest = {
      jurisdictionIds,
    };

    Logging.Info('PUT', url, jurisdictionsRequest);
    return this.fetch.putToService<void>(Service.ECM, url, jurisdictionsRequest);
  }
}
